import { ArcRotateCamera, Color3, type Mesh, MeshBuilder, type Scene, SkeletonViewer, TransformNode, Vector3 } from "@babylonjs/core";
import type { AssetInfo } from "../net/api";
import { $inspector, initialState, type InspectorState } from "./inspectorState";
import { clipInfo, type LoadedModel, loadGLB, type ModelLoader, roots } from "./models";

const TICK_MS = 100;
const RULER_M = 1.8;

interface Helpers { grid: Mesh; ruler: TransformNode; viewers: SkeletonViewer[] }

/** The model on the turntable, its clips and the helpers (spec 2o §2.4, §4.4). */
export class Inspector {
  readonly camera: ArcRotateCamera;
  private model: LoadedModel | null = null;
  private holder: TransformNode | null = null;
  private helpers: Helpers | null = null;
  private applied: InspectorState | null = null;
  private unsub: (() => void) | null = null;
  private timer: ReturnType<typeof setInterval> | null = null;
  private gen = 0;

  constructor(private readonly scene: Scene, private readonly canvas: HTMLCanvasElement, private readonly top: Vector3, private readonly load: ModelLoader = loadGLB) {
    this.camera = new ArcRotateCamera("inspector", -Math.PI / 2, Math.PI / 2.6, 3, top.clone(), scene);
    this.camera.minZ = 0.05;
    this.camera.lowerRadiusLimit = 0.3;
    this.camera.wheelPrecision = 40;
  }

  isOpen(): boolean { return $inspector.get() !== null; }

  async open(asset: AssetInfo): Promise<void> {
    this.close();
    const gen = ++this.gen;
    $inspector.set({ code: asset.code, name: asset.name, status: "loading", clips: [], hasSkeleton: false, report: asset.inspect ?? null, state: initialState([]) });
    this.scene.activeCamera = this.camera;
    this.camera.attachControl(this.canvas, true);
    let model: LoadedModel;
    try {
      model = await this.load(this.scene, asset.code);
    } catch {
      const v = $inspector.get();
      if (gen === this.gen && v) $inspector.set({ ...v, status: "failed" });
      return;
    }
    if (gen !== this.gen) { model.dispose(); return; }
    this.model = model;
    this.holder = new TransformNode("inspector:model", this.scene);
    for (const m of roots(model)) m.parent = this.holder;
    for (const m of model.meshes) { m.isPickable = false; m.checkCollisions = false; }
    const { min, max } = this.holder.getHierarchyBoundingVectors(true);
    this.holder.position.set(this.top.x - (min.x + max.x) / 2, this.top.y - min.y, this.top.z - (min.z + max.z) / 2);
    const size = max.subtract(min);
    this.camera.target = new Vector3(this.top.x, this.top.y + size.y / 2, this.top.z);
    this.camera.radius = Math.max(size.x, size.y, size.z) * 1.8 + 0.8;
    this.helpers = this.buildHelpers(size);
    const clips = clipInfo(model.groups);
    $inspector.set({ ...$inspector.get()!, status: "ready", clips, hasSkeleton: model.skeletons.length > 0, state: initialState(clips) });
    this.unsub = $inspector.subscribe((v) => { if (v) this.apply(v.state); });
    this.timer = setInterval(() => this.tick(), TICK_MS);
  }

  close(): void {
    this.gen++;
    this.unsub?.();
    this.unsub = null;
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
    if (this.helpers) {
      for (const v of this.helpers.viewers) v.dispose();
      this.helpers.grid.dispose();
      this.helpers.ruler.dispose();
      this.helpers = null;
    }
    this.model?.dispose();
    this.model = null;
    this.holder?.dispose();
    this.holder = null;
    this.applied = null;
    this.camera.detachControl();
    $inspector.set(null);
  }

  private fps(i: number): number { return this.model?.groups[i]?.targetedAnimations[0]?.animation.framePerSecond ?? 60; }

  private apply(s: InspectorState): void {
    const groups = this.model?.groups ?? [];
    const prev = this.applied;
    const g = s.clip >= 0 ? groups[s.clip] : undefined;
    const fps = this.fps(s.clip);
    if (!prev || prev.clip !== s.clip) {
      for (const other of groups) other.stop();
      if (g) {
        g.start(true, s.speed, g.from, g.to);
        g.goToFrame(g.from + s.time * fps);
        if (!s.playing) g.pause();
      }
    } else if (g) {
      if (prev.speed !== s.speed) g.speedRatio = s.speed;
      if (prev.playing !== s.playing) { if (s.playing) g.play(true); else g.pause(); }
      if (!s.playing && prev.time !== s.time) g.goToFrame(g.from + s.time * fps);
    }
    if (this.helpers) {
      this.helpers.grid.setEnabled(s.grid);
      this.helpers.ruler.setEnabled(s.ruler);
      for (const v of this.helpers.viewers) v.isEnabled = s.skeleton;
    }
    this.applied = s;
  }

  /** While a clip plays, the timeline follows it ten times a second. */
  private tick(): void {
    const v = $inspector.get();
    const g = v && v.state.clip >= 0 ? this.model?.groups[v.state.clip] : undefined;
    if (!v || !g || !v.state.playing) return;
    const frame = g.animatables[0]?.masterFrame ?? g.from;
    const time = Math.max(0, (frame - g.from) / this.fps(v.state.clip));
    if (Math.abs(time - v.state.time) > 1e-3) $inspector.set({ ...v, state: { ...v.state, time } });
  }

  private buildHelpers(size: Vector3): Helpers {
    const t = this.top, half = 3, y = t.y + 0.002;
    const lines: Vector3[][] = [];
    for (let i = -half; i <= half; i++) {
      lines.push([new Vector3(t.x + i, y, t.z - half), new Vector3(t.x + i, y, t.z + half)]);
      lines.push([new Vector3(t.x - half, y, t.z + i), new Vector3(t.x + half, y, t.z + i)]);
    }
    const grid = MeshBuilder.CreateLineSystem("inspector:grid", { lines }, this.scene);
    grid.color = new Color3(0.55, 0.55, 0.6);
    grid.isPickable = false;

    const ruler = new TransformNode("inspector:ruler", this.scene);
    const x = t.x + Math.max(size.x / 2, 0.3) + 0.3;
    const pole = MeshBuilder.CreateCylinder("inspector:ruler-pole", { diameter: 0.03, height: RULER_M }, this.scene);
    pole.position.set(x, t.y + RULER_M / 2, t.z);
    const ticks: Vector3[][] = [];
    for (let i = 0; i <= 18; i++) {
      const w = i % 5 === 0 ? 0.12 : 0.06; // longer marks every half metre
      ticks.push([new Vector3(x - w, t.y + i / 10, t.z), new Vector3(x + w, t.y + i / 10, t.z)]);
    }
    const marks = MeshBuilder.CreateLineSystem("inspector:ruler-marks", { lines: ticks }, this.scene);
    for (const m of [pole, marks]) { m.parent = ruler; m.isPickable = false; }

    const viewers = (this.model?.skeletons ?? []).flatMap((sk) => {
      const mesh = this.model!.meshes.find((m) => m.skeleton === sk);
      if (!mesh) return [];
      const v = new SkeletonViewer(sk, mesh as Mesh, this.scene, false, 3, { displayMode: SkeletonViewer.DISPLAY_LINES });
      v.color = Color3.Yellow();
      v.isEnabled = false;
      return [v];
    });
    return { grid, ruler, viewers };
  }
}
